import { NgIf } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { latLngBounds, LatLngBounds } from "leaflet";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { OfflineModeService } from "../../services/offline-mode.service";
import { PROJECT_PROVIDER, DATA_PROVIDER, DataProvider, DOWNLOAD_PROGRESS_COMPONENT } from "../../core";
import { LayerService } from "../services/layer.service";
import { TransformationService } from "../services/transformation.service";
import { PointStoreComponent } from "../point-store/point-store.component";
import { ProjectPointsService } from "./project-points.service";

// Re-export interfaces from service for backwards compatibility
export { BoundsSelection, ProjectPointSelection } from "./project-points.service";

@Component({
	selector: 'bp-project-points',
	templateUrl: 'project-points.component.html',
	styleUrls: ['project-points.component.css', 'project-manager-section.style.css'],
	imports: [NgIf, FormsModule],
	standalone: true
})
export class ProjectPointsComponent {
	constructor(
		public pps: ProjectPointsService,
		public ls: LayerService,
		public oms: OfflineModeService,
		@Inject(PROJECT_PROVIDER) public ps: any,
		@Inject(DATA_PROVIDER) private ds: DataProvider,
		private ts: TransformationService,
		private modalService: NgbModal,
		@Inject(DOWNLOAD_PROGRESS_COMPONENT) private downloadProgressComponent: any
	) {}

	// === UI Actions ===

	async downloadPoints(): Promise<void> {
		if (this.oms.offlineMode()) return;
		if (this.pps.pointsDownloadStatus() === 'complete') return;

		const filteredPointIds = await this.pps.getFilteredProjectPointIds();

		const progressModalRef = this.modalService.open(this.downloadProgressComponent, {
			backdrop: 'static',
			keyboard: false
		});
		progressModalRef.componentInstance.skipTileDownload = true;
		progressModalRef.componentInstance.skipPointsDownload = false;
		progressModalRef.componentInstance.expectedPointsCount = filteredPointIds.length;

		const hasPoints = filteredPointIds.length > 0;

		if (hasPoints) {
			await this.ds.downloadDocumentsByIds(filteredPointIds);
		} else {
			await this.ds.ensureEmptyOfflineDatabase();
		}

		await progressModalRef.result.catch(() => {});
		this.pps.updatePointsDownloadStatus();
	}

	openPointStore() {
		const previousState = this.pps.serializeSelection();

		const modalRef = this.modalService.open(PointStoreComponent, { size: 'lg' });
		const instance = modalRef.componentInstance as PointStoreComponent;
		instance.setParentSignals(
			this.pps.boundsSelections,
			this.pps.activeRegionIndices,
			this.pps.regionPointIds,
			this.pps.regionFilters,
			this.pps.regionNames,
			this.pps.globalFilterName,
			this.pps.useGlobalFilter
		);
		instance.filtersChanged.subscribe(async () => {
			await this.pps.saveSelection(false);
			this.ls.regionNamesChanged.emit();
		});
		modalRef.closed.subscribe(async () => {
			const currentState = this.pps.serializeSelection();
			const hasChanged = previousState !== currentState;
			await this.pps.updateFilteredBoundsPointIds();
			this.pps.saveSelection(hasChanged);
		});
	}

	toggleRegionPoints(enabled: boolean) {
		this.pps.toggleRegionPoints(enabled);
	}

	zoomToAllPoints() {
		if (this.pps.totalCount() === 0) return;
		this.zoomToPoints([...this.pps.allProjectPointIds()]);
	}

	zoomToRegionPoints() {
		if (this.pps.regionCount() === 0) return;
		this.zoomToPoints(this.pps.regionPointIds());
	}

	zoomToBoundsPoints() {
		if (this.pps.boundsCount() === 0) return;
		const allBoundsIds = this.pps.boundsSelections()
			.filter(s => s.active)
			.flatMap(s => s.pointIds);
		this.zoomToPoints(allBoundsIds);
	}

	zoomToFilterPoints() {
		if (this.pps.filterCount() === 0) return;
		this.zoomToPoints(this.pps.filterPointIds());
	}

	zoomToAssignedPoints() {
		if (this.pps.assignedCount() === 0) return;
		this.zoomToPoints(this.pps.assignedPointIds());
	}

	// === Helpers ===

	private async zoomToPoints(pointIds: string[]) {
		if (pointIds.length === 0) return;
		const pointIdSet = new Set(pointIds);
		const response: any = await this.ds.queryIndex();
		const rows = response?.rows ?? [];

		let bounds: LatLngBounds | null = null;
		rows.forEach((row: any) => {
			if (pointIdSet.has(row.id)) {
				const header = row.key[4];
				if (header?.koordinaten) {
					const ll = this.ts.from(header.koordinaten);
					if (!bounds) {
						bounds = latLngBounds(ll, ll);
					} else {
						bounds.extend(ll);
					}
				}
			}
		});
		if (bounds) {
			this.ls.regionClicked.emit(bounds);
		}
	}
}
